import React from 'react';
import { motion } from 'framer-motion';
import { NewsStoryProps } from './NewsStory';
import { MotionImage, useSanitizedTranslation } from 'ui';

export type FeaturedNewsLayoutProps = {
  stories: NewsStoryProps[];
  accent: string;
};

const imageVariants = {
  initial: {
    scale: 1.01,
    transition: { type: 'spring', damping: 65, stiffness: 1000, mass: 1, restDelta: 0.000001 },
  },
  hover: {
    scale: 1.05,
    transition: { type: 'spring', damping: 65, stiffness: 1000, mass: 1, restDelta: 0.000001 },
  },
};

export const FeaturedNewsLayout = ({ stories, accent }: FeaturedNewsLayoutProps) => {
  const { t } = useSanitizedTranslation();

  if (stories.length < 4) return null;

  const [story1, story2, story3, story4] = stories.slice(0, 4);

  const MainStoryCard = ({ story }: { story: NewsStoryProps }) => {
    const url = story.link ? story.link : (story.slug || '').replace(/^\/?/, '/');
    const linkOffsite = url.startsWith('/press') ? false : true;
    const storyDate = story.date
      ? new Date(story.date).toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        })
      : null;
    return (
      <motion.a
        initial="initial"
        animate="initial"
        whileHover="hover"
        href={url}
        target={linkOffsite ? '_blank' : '_self'}
        className="flex h-full w-full cursor-pointer flex-col items-start"
      >
        <motion.div className="bg-xumoBerry relative mb-6 flex aspect-video w-full flex-shrink-0 items-center justify-center overflow-hidden rounded-md object-cover">
          <MotionImage
            src={story.thumbnail.src}
            alt={story.thumbnail.alt ?? ''}
            fill
            objectFit="cover"
            variants={imageVariants}
            sizes="(max-width: 768px) 100vw, 66vw"
            priority
          />
        </motion.div>
        <div className="flex flex-1 flex-col gap-4">
          <h3
            className="type-subheading line-clamp-3 pb-[3px] text-left text-black"
            data-i18n={story.title}
            dangerouslySetInnerHTML={{ __html: t(story.title) }}
          />
          <div
            className="text-body-base mb-2 line-clamp-4 w-full overflow-hidden text-left text-[#60545E]"
            data-i18n={story.description}
            dangerouslySetInnerHTML={{ __html: t(story.description) }}
          />
          <p className="text-body-base text-xumoBlack mt-auto text-left opacity-60">{storyDate}</p>
        </div>
      </motion.a>
    );
  };

  const SideStoryWithImage = ({ story }: { story: NewsStoryProps }) => {
    const url = story.link ? story.link : (story.slug || '').replace(/^\/?/, '/');
    const linkOffsite = url.startsWith('/press') ? false : true;

    return (
      <motion.a
        initial="initial"
        animate="initial"
        whileHover="hover"
        href={url}
        target={linkOffsite ? '_blank' : '_self'}
        className="group flex w-full cursor-pointer flex-col items-start"
      >
        <motion.div className="bg-xumoBerry relative mb-4 flex aspect-video w-full items-center justify-center overflow-hidden rounded-md object-cover">
          <MotionImage
            src={story.thumbnail.src}
            alt={story.thumbnail.alt ?? ''}
            fill
            objectFit="cover"
            variants={imageVariants}
            sizes="(max-width: 768px) 100vw, 25vw"
          />
        </motion.div>
        <div className="flex flex-1 flex-col">
          <h3
            className="line-clamp-3  text-left text-lg font-semibold leading-tight text-black "
            data-i18n={story.title}
            dangerouslySetInnerHTML={{ __html: t(story.title) }}
          />
          <div
            className="text-body-base mb-2 mt-2 line-clamp-2 w-full overflow-hidden text-left text-[#60545E]"
            data-i18n={story.description}
            dangerouslySetInnerHTML={{ __html: t(story.description) }}
          />
          <span
            className="v group-hover:text-xumoBerry flex items-center gap-2 text-sm font-medium uppercase tracking-wide"
            style={{ color: accent || '#00C9B7' }}
          >
            Read More
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="10" viewBox="0 0 15 10" fill="none">
              <path
                d="M0 5.52H12.12L9.19 8.45L10.24 9.5L15 4.74L10.25 0L9.19 1.05L12.17 4.03H0V5.52Z"
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="0.5"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </motion.a>
    );
  };

  const SideStoryTextOnly = ({ story }: { story: NewsStoryProps }) => {
    const url = story.link ? story.link : (story.slug || '').replace(/^\/?/, '/');
    const linkOffsite = url.startsWith('/press') ? false : true;

    return (
      <motion.a
        initial="initial"
        animate="initial"
        whileHover="hover"
        href={url}
        target={linkOffsite ? '_blank' : '_self'}
        className="group flex w-full cursor-pointer flex-col items-start py-4"
      >
        <h3
          className="line-clamp-3 text-left text-lg font-semibold leading-tight text-black "
          data-i18n={story.title}
          dangerouslySetInnerHTML={{ __html: t(story.title) }}
        />
        <div
          className="text-body-base mb-2  mt-2 line-clamp-2 w-full overflow-hidden text-left text-[#60545E]"
          data-i18n={story.description}
          dangerouslySetInnerHTML={{ __html: t(story.description) }}
        />
        <span className="group-hover:text-xumoBerry flex items-center gap-2 text-sm font-medium uppercase tracking-wide" style={{ color: accent || '#00C9B7' }}>
          Read More
          <svg xmlns="http://www.w3.org/2000/svg" width="15" height="10" viewBox="0 0 15 10" fill="none">
            <path
              d="M0 5.52H12.12L9.19 8.45L10.24 9.5L15 4.74L10.25 0L9.19 1.05L12.17 4.03H0V5.52Z"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="0.5"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </motion.a>
    );
  };

  return (
    <div className="border-b border-gray-300 pb-8">
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <MainStoryCard story={story1} />
        </div>

        <div className="flex flex-col lg:col-span-1">
          <SideStoryWithImage story={story2} />

          <hr className="my-4 border-gray-300" />

          <SideStoryTextOnly story={story3} />

          <hr className="my-4 border-gray-300" />

          <SideStoryTextOnly story={story4} />
        </div>
      </div>
    </div>
  );
};
